<template>
  <div class="sms-manage">
    <!-- 工具栏 -->
    <div class="toolbar" v-show="!hideToolbar">
      <el-button type="success" icon="el-icon-plus" @click="handleAdd">
        添加短信签名
      </el-button>
    </div>

    <!-- 数据表格 -->
    <el-table
      :data="tableData"
      v-loading="loading"
      border
      stripe
      style="width: 100%"
      height="calc(100vh - 200px)"
    >
      <el-table-column prop="app_id" label="平台名称" width="180" align="center">
        <template slot-scope="scope">
          {{ formatAppName(scope.row.app_id) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="signature" label="短信签名" width="180" align="center" />
      
      <el-table-column prop="created_at" label="创建时间" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDate(scope.row.created_at) }}
        </template>
      </el-table-column>
      
      <el-table-column label="操作" width="140" align="center">
        <template slot-scope="scope">
          <span style="display:inline;line-height:30px;cursor:pointer;" @click="handleEdit(scope.row)">修改</span>&nbsp;&nbsp;
          <span style="display:inline;line-height:30px;cursor:pointer;" @click="handleDelete(scope.row)">删除</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      :current-page="pagination.page"
      :page-sizes="[10, 15, 20, 50]"
      :page-size="pagination.pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total"
      style="margin-top: 20px; text-align: right"
    />

    <!-- 编辑对话框 -->
    <SmsEditDialog
      :visible.sync="editDialogVisible"
      :sms-data="currentSms"
      :app-info="appInfo"
      @refresh="loadSmsList"
    />
  </div>
</template>

<script>
import { getSmsList, deleteSms, getAppList } from '@/api/custom-frontend'
import SmsEditDialog from './SmsEditDialog.vue'

export default {
  name: 'SmsManage',
  components: {
    SmsEditDialog
  },
  props: {
    appInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      tableData: [],
      appList: [], // 应用列表
      hideToolbar: false, // 控制工具栏显示隐藏
      pagination: {
        page: 1,
        pageSize: 15,
        total: 0
      },
      editDialogVisible: false,
      currentSms: null
    }
  },
  mounted() {
    this.getAppList()
  },
  watch: {
    appInfo: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadSmsList()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadSmsList() {
      if (!this.appInfo.id) return
      
      this.loading = true
      try {
        const params = {
          app_id: this.appInfo.id,
          page: this.pagination.page,
          rows: this.pagination.pageSize
        }
        
        const res = await getSmsList(params)
        if (res && res.err_code === 0) {
          this.tableData = res.rows || []
          this.pagination.total = res.total || 0
          // 根据老项目逻辑：如果有数据则隐藏工具栏
          this.hideToolbar = this.tableData.length > 0
        }
      } catch (error) {
        console.error('获取短信签名列表失败:', error)
        this.$message.error('获取短信签名列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取应用列表
    async getAppList() {
      try {
        const res = await getAppList()
        if (res && res.err_code === 0) {
          this.appList = res.rows || []
        }
      } catch (error) {
        console.error('获取应用列表失败:', error)
      }
    },
    
    handleAdd() {
      this.currentSms = null
      this.editDialogVisible = true
    },
    
    handleEdit(row) {
      this.currentSms = { ...row }
      this.editDialogVisible = true
    },
    
    // 格式化应用名称
    formatAppName(appId) {
      const app = this.appList.find(item => item.id === appId)
      return app ? app.name : ''
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('确认要删除该短信签名吗？', '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await deleteSms({ id: row.id })
        if (res && res.err_code === 0) {
          this.$message.success('删除成功')
          this.loadSmsList()
        } else {
          this.$message.error(res.err_msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除短信签名失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadSmsList()
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadSmsList()
    },

    
    formatDate(value) {
      if (!value) return '-'
      
      let date
      
      // 处理不同的时间格式
      if (typeof value === 'string') {
        // 字符串格式，可能是 ISO 格式或其他格式
        date = new Date(value)
      } else if (typeof value === 'number') {
        // 数字格式，可能是秒级或毫秒级时间戳
        if (value.toString().length === 10) {
          // 秒级时间戳
          date = new Date(value * 1000)
        } else {
          // 毫秒级时间戳
          date = new Date(value)
        }
      } else {
        // 其他格式，尝试直接转换
        date = new Date(value)
      }
      
      // 检查日期是否有效
      if (isNaN(date.getTime())) {
        return '-'
      }
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style lang="less" scoped>
.sms-manage {
  padding: 20px;
  height: 100%;
  
  .toolbar {
    padding: 8px 0;
    margin-bottom: 20px;
    border-bottom: 1px solid #e6e6e6;
    
    .search-label {
      margin-right: 10px;
      font-weight: bold;
    }
    
    label {
      margin-right: 5px;
    }
    
    .el-button {
      margin-left: 5px;
    }
  }
}
</style>